import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bucket, GetSalesInsightsAggregatedRequest, SalesInsightsGroupedAggregate, SalesInsightsGroupedAggregateItem, useSalesInsightsGroupedAggregated } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateParam } from '../../../../helpers/dateUtils';
import LineBarChart, { LineBarChartDataItem } from '../charts/lineBarChart/LineBarChart';
import { DashboardContext } from '../../DashboardContext';
import { SettingsContext } from '../../../../context/SettingsContext';
import { getBucketFromRange } from '../../dashboardHelpers';

const NetMarginByLocation = () => {
    const { t } = useTranslation();
    const { calendarPreferences } = useContext(SettingsContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { refreshDate } = useContext(DashboardContext);

    const initialStartDate = useMemo(() => filters.periodFrom
        ? filters.periodFrom
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate, [filters.periodFrom]);

    const initialEndDate = useMemo(() => filters.periodTo
        ? filters.periodTo
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate, [filters.periodTo]);

    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'transactionVolume,netMargin',
        groupBy: 'location',
        startDate: formatDateParam(initialStartDate),
        endDate: formatDateParam(initialEndDate),
        locations: filters?.locations?.join(','),
        locationArea: filters?.locationArea?.id,
        businessStartHour: calendarPreferences.daysStart,
        bucket: getBucketFromRange(initialStartDate, initialEndDate)
    });

    const {
        data: groupedAggregateSalesResponse,
        isLoading,
        isError,
        refetch: fetchSalesInsightsGrouped
    } = useSalesInsightsGroupedAggregated<SalesInsightsGroupedAggregate>(params, 'net-margin-by-location-grouped-aggregate');

    useEffect(() => {

        const start = filters.periodFrom
            ? filters.periodFrom
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

        const end = filters.periodTo
            ? filters.periodTo
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

        setParams({
            ...params,
            startDate: formatDateParam(start),
            endDate: formatDateParam(end),
            locations: filters?.locations?.join(','),
            locationArea: filters?.locationArea?.id,
            bucket: getBucketFromRange(start, end)
        });
    }, [filters]);

    useEffect(() => { fetchSalesInsightsGrouped() }, [params]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesInsightsGrouped();
        }
    }, [refreshDate]);

    function transformData(dataItem: { dateTime: string } & SalesInsightsGroupedAggregateItem): LineBarChartDataItem {
        const result: LineBarChartDataItem = {
            dateTime: new Date(dataItem.dateTime),
            barData: 0
        };

        dataItem?.salesInsightsGrouped?.forEach(({ key, salesInsights }) => {
            result[key] = salesInsights?.netMargin ?? 0;
            result.barData += salesInsights?.transactionVolume ?? 0;
        });
        return result;
    }

    return (
        <LineBarChart
            title={t("screens.dashboarding.charts.netMarginByLocation")}
            currency={groupedAggregateSalesResponse?.currency}
            description={t("screens.dashboarding.charts.excludingTax")}
            data={groupedAggregateSalesResponse?.salesInsightsGroupedAggregate?.map(transformData) ?? []}
            isLoading={isLoading}
            isError={isError}
            rightAxisLabel={t("screens.dashboarding.charts.volume")}
            leftAxisLabel={t("screens.dashboarding.charts.amount")}
            barLabel='Volume'
            bucket={params.bucket as Bucket}
            setBucket={(newView: Bucket) => setParams({ ...params, bucket: newView })}
            height={460}
            textLink={t('screens.dashboarding.charts.netMarginByLocation')}
            linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesByLocation.aspx`}
        />
    )
}

export default NetMarginByLocation;
